import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import {
    CarryOutOutlined,
    UserOutlined,
    CarOutlined,
    ApartmentOutlined,
    ClockCircleOutlined,
    DownloadOutlined
} from '@ant-design/icons';
import MainLayout from '../../../layouts/MainLayout';
import Loading from "../../../components/Loading";
import { ErrorInfo } from "../../../components/ErrorInfo";
import { NoPermission } from "../../../components/NoPermission";
import WarehouseSelector, { WarehouseDisplayMode } from '../../../components/WarehouseSelector';
import { WarehouseProvider } from '../../../contexts/WarehouseContext';
import { t } from '../../../i18n';
import { findFirstAvailableMenuItem } from '../../../utils/menuUtils';
import { hasResource } from '../../../utils/resourceAuth';
import { ResourceCode } from '../../../constants/resourceCodes';
import { getTmsHubStorageKey } from '../../../utils/auth';
import { useCurrentUserOperableHubs } from "../../../hooks/useCurrentUserOperableWarehouses";

const TmsLayout: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // 获取hub数据
    const { warehouses: hubs, loading, error } = useCurrentUserOperableHubs({
        fetchPolicy: 'network-only'
    });

    const [currentHub, setCurrentHub] = useState<any>(null);

    // 处理hub选择变化
    const handleHubChange = useCallback((hubId: string, siteType: number) => {
        console.log('📦 TmsLayout Hub选择:', { hubId, siteType });

        const selectedHub = hubs.find(h => h.id?.toString() === hubId);
        if (selectedHub) {
            setCurrentHub(selectedHub);
            const hubStorageKey = getTmsHubStorageKey();
            if (hubStorageKey) {
                localStorage.setItem(hubStorageKey, hubId);
            }
        }
    }, [hubs]);

    // 初始化时从localStorage恢复选中的hub（按登录用户id隔离），如果没有则选择第一个hub
    useEffect(() => {
        if (hubs.length > 0 && !currentHub) {
            const hubStorageKey = getTmsHubStorageKey();
            const savedHubId = hubStorageKey ? localStorage.getItem(hubStorageKey) : null;
            let hubToSelect = null;

            if (savedHubId) {
                // 尝试使用保存的hub ID
                hubToSelect = hubs.find(h => h.id?.toString() === savedHubId);
            }

            // 如果保存的hub不存在或无效，使用第一个hub
            if (!hubToSelect) {
                hubToSelect = hubs[0];
            }

            console.log('📦 TmsLayout初始化Hub选择:', {
                savedHubId,
                selectedId: hubToSelect.id,
                siteType: hubToSelect.siteType
            });
            setCurrentHub(hubToSelect);
            // 如果保存的hub无效，更新localStorage
            if (hubStorageKey && (!savedHubId || savedHubId !== hubToSelect.id?.toString())) {
                localStorage.setItem(hubStorageKey, hubToSelect.id?.toString() || '');
            }
        }
    }, [hubs, currentHub]);

    const menuItems = useMemo(() => [
        {
            key: 'task',
            label: t('tms.menu.task'),
            icon: <CarryOutOutlined />,
            display: hasResource([ResourceCode.menu.TMS_TASK]),
            children: [
                {
                    key: 'task/shipFromHere',
                    label: t('tms.menu.shipFromHere'),
                    display: hasResource([ResourceCode.menu.TMS_TASK_DEPARTURE]),
                    onClick: () => navigate('/tms/task/shipFromHere')
                },
                {
                    key: 'task/shipToHere',
                    label: t('tms.menu.shipToHere'),
                    display: hasResource([ResourceCode.menu.TMS_TASK_ARRIVAL]),
                    onClick: () => navigate('/tms/task/shipToHere')
                }
            ]
        },
        {
            key: 'route',
            label: t('tms.menu.route'),
            icon: <ApartmentOutlined />,
            display: hasResource([ResourceCode.menu.TMS_ROUTE]),
            onClick: () => navigate('/tms/route')
        },
        {
            key: 'schedule',
            label: t('tms.menu.schedule'),
            icon: <ClockCircleOutlined />,
            display: hasResource([ResourceCode.menu.TMS_SHIFT]),
            onClick: () => navigate('/tms/schedule')
        },
        {
            key: 'personnel',
            label: t('tms.menu.personnel'),
            icon: <UserOutlined />,
            display: hasResource([ResourceCode.menu.TMS_STAFF]),
            onClick: () => navigate('/tms/personnel')
        },
        {
            key: 'vehicle',
            label: t('tms.menu.vehicle'),
            icon: <CarOutlined />,
            display: hasResource([ResourceCode.menu.TMS_VEHICLE]),
            onClick: () => navigate('/tms/vehicle')
        },
        {
            key: 'download',
            label: t('tms.menu.download'),
            icon: <DownloadOutlined />,
            display: hasResource([ResourceCode.menu.TMS_DOWNLOAD]),
            onClick: () => navigate('/tms/download')
        }
    ], [navigate, t]);

    useEffect(() => {
        const isRoot = location.pathname === '/tms' || location.pathname === '/tms/';
        if (!isRoot) return;
        const first = findFirstAvailableMenuItem(menuItems);
        if (first?.onClick) first.onClick();
    }, [location.pathname, menuItems]);

    // 二级菜单一个可点击项都没有（资源码不够）时，视为无权限，兜底展示 NoPermission 而不是空白页
    const hasVisibleMenu = useMemo(
        () => findFirstAvailableMenuItem(menuItems) !== null,
        [menuItems]
    );

    // Header布局，包含Hub选择器
    const hubSelector = (
        <div className="flex items-center gap-4 px-4 py-2">
            <WarehouseSelector
                onWarehouseChange={handleHubChange}
                displayMode={WarehouseDisplayMode.HUB_ONLY}
                warehouses={hubs}
                selectedWarehouseId={currentHub?.id?.toString()}
            />
        </div>
    );

    return (
        <WarehouseProvider currentWarehouse={currentHub}>
            <MainLayout
                activeFirstMenu="task"
                secondMenu={menuItems}
                secondHeader={hubSelector}
            >
                <div className="p-6 h-full flex flex-col">
                    {loading ? <Loading/> : (error ? <ErrorInfo error={error}/> : (!hasVisibleMenu ? <NoPermission/> : <Outlet/>))}
                </div>
            </MainLayout>
        </WarehouseProvider>
    );
};

export default TmsLayout;